import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Modal,
  NumberInput,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuSparkles, LuTrash2, LuTriangleAlert } from 'react-icons/lu'
import { type DraftInvoicePayload, useDraftInvoice } from '../../hooks/useAi'
import { useCreateInvoice } from '../../hooks/useInvoices'
import { humanizeError } from '../../lib/errors'
import { CurrencyText, formatIdr } from './CurrencyText'

interface Props {
  contractId: string | null
  opened: boolean
  onClose: () => void
}

type Period = 'this-month' | 'next-month'

export function AiDraftInvoiceModal({ contractId, opened, onClose }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const draftMut = useDraftInvoice()
  const createMut = useCreateInvoice()
  const [period, setPeriod] = useState<Period>('next-month')
  const [draft, setDraft] = useState<DraftInvoicePayload | null>(null)
  const [periodLabel, setPeriodLabel] = useState<string>('')

  // Trigger AI draft whenever the modal opens or period changes
  useEffect(() => {
    if (!opened || !contractId) return
    let cancelled = false
    setDraft(null)
    draftMut.mutate(
      { contractId, period },
      {
        onSuccess: (res) => {
          if (cancelled) return
          setDraft(res.draft)
          setPeriodLabel(res.context.periodLabel)
        },
        onError: () => {
          /* error shown inline */
        },
      },
    )
    return () => {
      cancelled = true
    }
    // biome-ignore lint/correctness/useExhaustiveDependencies: draftMut is a stable mutation handle from react-query
  }, [opened, contractId, period, draftMut.mutate])

  const updateLine = (idx: number, patch: Partial<DraftInvoicePayload['lineItems'][number]>) => {
    setDraft((d) => {
      if (!d) return d
      const lineItems = d.lineItems.map((li, i) => (i === idx ? { ...li, ...patch } : li))
      return { ...d, lineItems }
    })
  }

  const removeLine = (idx: number) => {
    setDraft((d) => (d ? { ...d, lineItems: d.lineItems.filter((_, i) => i !== idx) } : d))
  }

  const subtotal = (draft?.lineItems ?? []).reduce((s, li) => s + li.quantity * li.unitPriceIdr, 0)
  const taxRate = draft?.taxRate ?? 11
  const tax = (subtotal * taxRate) / 100
  const total = subtotal + tax

  const handleCreate = () => {
    if (!draft) return
    createMut.mutate(
      {
        customerId: draft.customerId,
        boatId: draft.boatId ?? undefined,
        dueDate: draft.dueDate,
        taxRate: draft.taxRate,
        currency: 'IDR',
        notes: draft.notes ?? undefined,
        recipientName: draft.recipientName ?? undefined,
        recipientEmail: draft.recipientEmail ?? undefined,
        lineItems: draft.lineItems,
      },
      {
        onSuccess: (res) => {
          notifications.show({
            title: t('page.accounts.contracts.ai_draft_success'),
            message: res.invoice.code,
            color: 'teal',
          })
          onClose()
          navigate({ to: `/dashboard/billing/invoices/${res.invoice.id}` as any })
        },
        onError: (e) =>
          notifications.show({
            title: t('common.failed'),
            message: humanizeError(e, t),
            color: 'red',
          }),
      },
    )
  }

  const isDisabledError =
    draftMut.isError &&
    draftMut.error instanceof Error &&
    (draftMut.error.message.includes('AI_DISABLED') || draftMut.error.message.includes('not configured'))

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <ThemeIcon variant="light" color="laut" size="md" radius="md">
            <LuSparkles size={16} />
          </ThemeIcon>
          <Text fw={600}>{t('page.accounts.contracts.ai_draft_title')}</Text>
        </Group>
      }
      size="lg"
      centered
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {t('page.accounts.contracts.ai_draft_subtitle')}
        </Text>

        <Group gap="xs" align="center">
          <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
            {t('page.accounts.contracts.ai_draft_period_label')}
          </Text>
          <SegmentedControl
            size="xs"
            value={period}
            onChange={(v) => setPeriod(v as Period)}
            data={[
              { value: 'this-month', label: t('page.accounts.contracts.ai_draft_period_this') },
              { value: 'next-month', label: t('page.accounts.contracts.ai_draft_period_next') },
            ]}
          />
          {periodLabel && (
            <Badge variant="light" color="laut">
              {periodLabel}
            </Badge>
          )}
        </Group>

        {draftMut.isPending && (
          <Group gap="sm" py="md">
            <Loader size="sm" color="laut" />
            <Text size="sm" c="dimmed">
              {t('page.accounts.contracts.ai_draft_loading')}
            </Text>
          </Group>
        )}

        {draftMut.isError && (
          <Group gap="xs" py="sm" px="md" style={{ borderRadius: 8, background: 'var(--mantine-color-red-0)' }}>
            <ThemeIcon variant="light" color="red" size="sm" radius="xl">
              <LuTriangleAlert size={13} />
            </ThemeIcon>
            <Text size="sm" c="red.7">
              {isDisabledError
                ? t('page.accounts.contracts.ai_draft_disabled')
                : t('page.accounts.contracts.ai_draft_failed')}
            </Text>
          </Group>
        )}

        {draft && (
          <Stack gap="sm">
            <Box style={{ overflowX: 'auto' }}>
              <Table withTableBorder verticalSpacing="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('page.accounts.contracts.ai_draft_col_desc')}</Table.Th>
                    <Table.Th ta="right" style={{ minWidth: 70 }}>
                      {t('page.accounts.contracts.ai_draft_col_qty')}
                    </Table.Th>
                    <Table.Th ta="right" style={{ minWidth: 130 }}>
                      {t('page.accounts.contracts.ai_draft_col_price')}
                    </Table.Th>
                    <Table.Th ta="right" style={{ minWidth: 130 }}>
                      {t('page.accounts.contracts.ai_draft_col_amount')}
                    </Table.Th>
                    <Table.Th style={{ width: 30 }} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {draft.lineItems.map((li, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: AI line items have no stable id; index is fine within a single draft session
                    <Table.Tr key={i}>
                      <Table.Td>
                        <TextInput
                          variant="unstyled"
                          value={li.description}
                          onChange={(e) => updateLine(i, { description: e.currentTarget.value })}
                        />
                      </Table.Td>
                      <Table.Td ta="right">
                        <NumberInput
                          variant="unstyled"
                          value={li.quantity}
                          onChange={(v) => updateLine(i, { quantity: typeof v === 'number' ? v : 0 })}
                          min={0}
                          step={1}
                          hideControls
                          styles={{ input: { textAlign: 'right' } }}
                        />
                      </Table.Td>
                      <Table.Td ta="right">
                        <NumberInput
                          variant="unstyled"
                          value={li.unitPriceIdr}
                          onChange={(v) => updateLine(i, { unitPriceIdr: typeof v === 'number' ? v : 0 })}
                          min={0}
                          step={100_000}
                          thousandSeparator=","
                          hideControls
                          styles={{ input: { textAlign: 'right' } }}
                        />
                      </Table.Td>
                      <Table.Td ta="right">
                        <CurrencyText idr={li.quantity * li.unitPriceIdr} fw={500} />
                      </Table.Td>
                      <Table.Td>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          size="sm"
                          onClick={() => removeLine(i)}
                          aria-label="Remove"
                        >
                          <LuTrash2 size={13} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Box>

            <Stack gap={4} align="flex-end">
              <Group gap="lg">
                <Text size="sm" c="dimmed">
                  {t('page.accounts.contracts.ai_draft_subtotal')}
                </Text>
                <Text size="sm" fw={500}>
                  {formatIdr(subtotal)}
                </Text>
              </Group>
              <Group gap="lg">
                <Text size="sm" c="dimmed">
                  {t('page.accounts.contracts.ai_draft_tax', { rate: taxRate })}
                </Text>
                <Text size="sm" fw={500}>
                  {formatIdr(tax)}
                </Text>
              </Group>
              <Group gap="lg">
                <Text size="sm" fw={600}>
                  {t('page.accounts.contracts.ai_draft_total')}
                </Text>
                <Text size="md" fw={700} c="laut.7">
                  {formatIdr(total)}
                </Text>
              </Group>
            </Stack>

            <Group gap="sm" justify="space-between" align="center">
              <Text size="xs" c="dimmed">
                {t('page.accounts.contracts.ai_draft_due_date')}: {dayjs(draft.dueDate).format('DD MMM YYYY')}
              </Text>
            </Group>
          </Stack>
        )}

        <Group justify="flex-end" gap="sm" mt="sm">
          <Button variant="default" onClick={onClose}>
            {t('page.accounts.contracts.ai_draft_cancel')}
          </Button>
          <Button
            color="laut"
            leftSection={<LuSparkles size={14} />}
            onClick={handleCreate}
            loading={createMut.isPending}
            disabled={!draft || draft.lineItems.length === 0}
          >
            {t('page.accounts.contracts.ai_draft_create')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
